import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateCustomer } from 'src/api/customer'
import { invalidateLocalityCache } from 'src/api/localities'
import { invalidateScheduledTaskCache } from 'src/api/scheduledTask'
import { AppForm } from 'src/components/FormCore'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { MainButton } from 'src/components/MainButton'
import { TaskTypes } from 'src/enums'
import { IMainComponent } from 'src/generated-types/main-component'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { useZodForm } from 'src/hooks/zod-form'
import AddVesselTemporaryForm from 'src/screens/localities/tasks/AddVesselTemporaryForm'
import { ShowDialogProps } from 'src/show-dialog'
import { Flex } from 'src/styles/flexComponent'
import { dateWithoutTimeConvert } from 'src/utils/function'
import { z } from 'zod'
import { invalidateNotificationCache } from 'src/api/notification'
import { invalidateTaskCache, useCreateTask, useValidateVesselTimeline } from '../../../../api/tasks'
import { ALREADY_EXISTS, INVALID_END_DATE, SUCCESSFULLY_ADDED } from '../../../../constant'
import InformationForm from './InformationForm'
import { taskTypeList } from './task-constants'

export const durationTimeline = [1, 5, 7, 14, 21, 30, 60, 90, 120, 180, 365]

interface AddTasksProps extends ShowDialogProps {
  customerId: any
  localityId?: EntityId
}

const AddTasksDialog: React.FC<AddTasksProps> = ({ pop, customerId, localityId }) => {
  const { mutateAsync } = useCreateTask(customerId)
  const { mutateAsync: validateVesselTimeline } = useValidateVesselTimeline(localityId as EntityId)
  const { t: translate } = useTranslation(['task', 'common'])
  const [formType, setFormType] = useState<TaskTypes>(TaskTypes.Once)
  const [isDurationDisabled, setIisDurationDisabled] = useState(false)
  const [vessels, setVessels] = useState<any[]>([])
  const [selectedEquipments, setSelectedEquipments] = useState<IMainComponent[]>([])

  const { showDialog } = useDialogCtx()

  const genericSchema = z.object({
    id: z.number(),
    name: z.string(),
  })
  const vesselSchema = z.object({
    resourceId: z.number(),
    description: z.string().max(400).optional().nullable(),
    resource: z.object({
      id: z.number(),
      name: z.string(),
    }),
    resourceDescription: z.string(),
    startDate: z.string().or(z.date()).optional(),
  })

  useEffect(() => {
    form.setValue('endDate', undefined)
    form.setValue('interval', undefined)
    setIisDurationDisabled(false)
  }, [formType])

  const taskSchema = z
    .object({
      title: z
        .string()
        .nonempty(translate('nameIsRequired', { ns: 'common' }))
        .max(200),
      description: z
        .string()
        .nonempty(translate('descriptionIsRequired', { ns: 'common' }))
        .max(400),
      localityIds: z.array(genericSchema).min(1),
      responsiblePerson: z
        .object({
          id: z.string(),
          email: z.string().email(),
          firstName: z.string().nullable(),
          lastName: z.string().nullable(),
        })
        .optional(),
      formSchemaIds: z.array(genericSchema).optional().nullable(),
      type: z.number().default(TaskTypes.Once),
      interval: z
        .object({
          value: z.number(),
        })
        .optional()
        .refine(data => formType == TaskTypes.Once || (formType == TaskTypes.Repeatable && data != null), {
          message: translate('required', { ns: 'common' }),
        }),
      startDate: z.date(),
      endDate: z.date().optional(),
      duration: z
        .string()
        .nonempty(translate('required', { ns: 'common' }))
        .refine(s => parseInt(s) > 0, { message: translate('durationShouldBePositive', { ns: 'task' }) }),
      fileIds: z.array(z.union([z.string(), z.number()])).optional(),
      files: z.array(z.any()).nullable().optional(),
      vessels: z.array(vesselSchema).optional(),
      equipmentIds: z.array(z.number()).optional(),
    })
    .refine(
      data =>
        !data.endDate ||
        dateWithoutTimeConvert(data.endDate).getTime() >= dateWithoutTimeConvert(data.startDate).getTime(),
      {
        message: translate('endStartDateError', { ns: 'common' }),
        path: ['endDate'],
      },
    )
    .refine(data => data.type == TaskTypes.Repeatable || data.duration != '', {
      message: translate('required', { ns: 'common' }),
      path: ['duration'],
    })
    .refine(
      data =>
        data.interval == undefined ||
        data.interval.value < 1 ||
        parseInt(data.duration) <= durationTimeline[data.interval.value],
      {
        message: translate('duration-validation', { ns: 'task' }),
        path: ['duration'],
      },
    )
  const form = useZodForm(taskSchema, { defaultValues: { type: formType } })

  const onSubmit = useCallback(
    async form => {
      form.interval = form?.interval?.value
      const { file: _, ...taskValues } = form
      form.localityIds = taskValues.localityIds.map(l => l.id)
      form.responsibleUserId = taskValues.responsiblePerson.id
      form.formSchemaIds = taskValues.formSchemaIds.map(l => l.id)
      const vesselSet = vessels.map(v => ({
        ...v,
        startDate: form.startDate,
      }))
      form.vessels = vesselSet
      form.equipmentIds = selectedEquipments.map(e => e.id as number)
      await mutateAsync(
        { ...taskValues, ...form },
        {
          onSuccess: ({ data }) => {
            if (data == SUCCESSFULLY_ADDED || Array.isArray(data)) {
              toast.success(translate('taskAddedSuccessfully', { ns: 'task' }))
              invalidateTaskCache.getCustomerAdminTaskByFilter(customerId)
              if (localityId) {
                invalidateScheduledTaskCache.useScheduledTasks(localityId)
              }
              invalidateTaskCache.getCustomerAdminScheduleTaskByFilter(customerId)
              invalidateTaskCache.useGetFilterCalendarScheduleTask(customerId)
              invalidateScheduledTaskCache.invalidateAllScheduledTasks(localityId as EntityId)
              invalidateCustomer.getCustomersActivityCounts(customerId)
              invalidateLocalityCache.getMaintenanceTypesCount(customerId, localityId as EntityId)
              invalidateLocalityCache.getMaintenances(customerId, localityId as EntityId)
              invalidateLocalityCache.getLocalityWithActivityCounts(customerId, localityId as EntityId)
              invalidateNotificationCache(customerId)
              pop()
            }
          },
          onError: error => {
            if (error['status'] === 409) {
              const data = error['data']
              if (data) {
                const conflictedVessel = vessels.find(v => v.resourceId === data.resourceId)
                toast.error(conflictedVessel.resource.name + ' ' + translate('vessel-conflict-date', { ns: 'task' }))
              }
              return
            }
            if (error['data'] == INVALID_END_DATE) {
              toast.error(translate('invalid-end-date', { ns: 'task' }))
            } else {
              toast.error(translate('addingError', { ns: 'common' }))
            }
          },
        },
      )
    },
    [mutateAsync, translate, customerId, localityId, pop, formType, vessels, selectedEquipments],
  )

  const onAssignVessel = useCallback(() => {
    showDialog(AddVesselTemporaryForm, {
      title: translate('assign-resource', { ns: 'task' }),
      opts: {
        maxWidth: 'sm',
        fullWidth: true,
      },
      componentProps: {
        customerId: customerId,
        vessels: vessels,
        onSubmit: async formValues => {
          const timelineValidation = {
            startDate: form.getValues('startDate'),
            duration: form.getValues('duration'),
            resourceId: formValues.resourceId,
          }

          await validateVesselTimeline(
            { ...timelineValidation },
            {
              onError: error => {
                if (error['data'] == ALREADY_EXISTS) {
                  toast.error(translate('conflict-date', { ns: 'task' }))
                  return
                }
              },
            },
          )
          const newVesselId = vessels.length > 0 ? vessels[vessels.length - 1].id + 1 : 0
          setVessels(prevVessels => [...prevVessels, { id: newVesselId as number, ...formValues }])
        },
      },
    })
  }, [customerId, localityId, mutateAsync, showDialog, translate, vessels])

  return (
    <GenericDialogLayout
      title={translate('add-task', { ns: 'task' })}
      pop={pop}
      removedCloseButton={false}
      actions={[
        <MainButton
          key="locality-form-button"
          variant="contained"
          onClick={async () => {
            form.handleSubmit(await onSubmit)()
          }}
        >
          {translate('add', { ns: 'common' })}
        </MainButton>,
      ]}
      topActions={[
        <Flex.Row key={'top-action-list'} width={'100%'} px={4}>
          <FormControl key={'task-type-buttons-group'}>
            <RadioGroup
              value={formType}
              onChange={(_, v) => {
                setFormType(v as unknown as TaskTypes)
                form.setValue('type', parseInt(v))
              }}
              row
              name="task-type-buttons-group"
              sx={{ gap: 2 }}
            >
              {taskTypeList.map((taskType, index) => {
                return (
                  <FormControlLabel
                    key={index}
                    value={taskType.value}
                    control={<Radio />}
                    label={`${taskType.option}`}
                  />
                )
              })}
            </RadioGroup>
          </FormControl>
        </Flex.Row>,
      ]}
    >
      <AppForm form={form} onSubmit={onSubmit}>
        <InformationForm
          customerId={customerId}
          localityId={localityId}
          formType={formType}
          isDurationDisabled={isDurationDisabled}
          setIisDurationDisabled={setIisDurationDisabled}
          vessels={vessels}
          setVessels={setVessels}
          onAssignVessel={onAssignVessel}
          selectedEquipments={selectedEquipments}
          setSelectedEquipments={setSelectedEquipments}
        />
      </AppForm>
    </GenericDialogLayout>
  )
}

export default AddTasksDialog
